/**
 * Respuestas que llegaron al número oficial: fecha, destino enmascarado, texto o botón tocado, y si
 * fue una baja, si se respondió sola o si no se pudo atribuir a una campaña. La que escribió el
 * simulador lleva «simulada»: una «BAJA» de prueba no se lee como una persona real que pidió la baja.
 *
 * Una foto, un audio o una ubicación no traen texto: la etiqueta sale del `tipo` («Foto», «Ubicación»…).
 * «(sin texto)» se leía como si la persona no hubiera mandado nada. Con `conversacionId`, «Ver conversación»
 * lleva a la bandeja con `?filtro=todas`: con el filtro por defecto («Por contestar») una conversación ya
 * respondida no aparecía en la lista de al lado. El número sigue enmascarado acá: fuera de Conversaciones
 * no cambia nada.
 */

import { Link } from "react-router-dom";
import { MessageSquareReply } from "lucide-react";
import type { RespuestaEntrante } from "../types";
import type { TipoEntrante } from "../types.conversaciones";
import { fechaHora } from "../../campana-nueva/utils/format";
import { destinoParaMostrar } from "../logic/formato";
import { R_CONVERSACIONES } from "../logic/conversaciones";

/** Lo que se muestra de un entrante sin texto, según lo que mandó. Los que no están acá dicen «(sin texto)». */
const ETIQUETA_SIN_TEXTO: Partial<Record<TipoEntrante, string>> = {
  imagen: "Foto", audio: "Audio", video: "Video", documento: "Documento", sticker: "Sticker", ubicacion: "Ubicación", contacto: "Contacto",
};

export function ListaRespuestas({ items, mostrarCampana = false }: { items: RespuestaEntrante[]; mostrarCampana?: boolean }) {
  if (!items?.length) {
    return <p className="py-6 text-center text-sm text-slate-400">Todavía no hay respuestas.</p>;
  }
  return (
    <ul className="divide-y divide-slate-100">
      {items.map((r) => (
        <li key={r.id} className="flex items-start gap-3 py-3">
          <span className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
            <MessageSquareReply size={15} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono text-slate-600">{destinoParaMostrar(r.destino, false)}</span>
              <span className="text-[11px] text-slate-400">{fechaHora(r.createdAt)}</span>
              {r.esBaja && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-600">Baja</span>}
              {r.autoRespondida && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Respondida automáticamente</span>}
              {r.atribucion === "sin_atribuir" && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Sin campaña</span>}
              {r.simulado === true && (
                <span title="La escribió el simulador: nadie la mandó por WhatsApp." className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">simulada</span>
              )}
            </div>
            <p className="text-sm text-slate-800 mt-0.5 break-words whitespace-pre-wrap">
              {r.texto && r.texto.trim()
                ? r.texto
                : r.buttonPayload
                  ? `Tocó «${r.buttonPayload}»`
                  : r.tipo && ETIQUETA_SIN_TEXTO[r.tipo]
                    ? <span className="text-slate-500 italic">{ETIQUETA_SIN_TEXTO[r.tipo]}</span>
                    : <span className="text-slate-400 italic">(sin texto)</span>}
            </p>
            {(mostrarCampana && r.campanaId) || r.conversacionId ? (
              <div className="flex items-center gap-3">
                {mostrarCampana && r.campanaId && (
                  <Link to={`/activa/oficial/campanas/${encodeURIComponent(r.campanaId)}`} className="text-[11px] text-violet-600 hover:underline">Ver campaña</Link>
                )}
                {r.conversacionId && (
                  <Link to={`${R_CONVERSACIONES}/${encodeURIComponent(r.conversacionId)}?filtro=todas`} className="text-[11px] text-violet-600 hover:underline">Ver conversación</Link>
                )}
              </div>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

export default ListaRespuestas;
